import React from 'react';
import { RendererProps } from '@atlaskit/renderer';
import { LinkExtension } from './link-extension';
import { IconExtension } from './icon-extension';
import get from 'lodash/get';
import { MediaExtension } from './media-extension';
import { TextFragmentExtension } from './text-fragment-extension';
import { getContentAri } from '../../utils/get-content-ari';
import type { ContentLinkAnalytics } from '../atlas-link/track';

export interface PositionTracker {
    sacLinkPosition: number;
}

/**
 * Helper: Extract localized field value (tries en-US first, then fallback)
 */
function getLocalizedField(obj: any, path: string): string {
    const enUsValue = get(obj, `${path}.en-US`);
    return enUsValue || get(obj, path, '');
}

/**
 * Handler: sac-link extension
 */
function handleSacLink(
    ext: any,
    data: any,
    tracker: PositionTracker
): React.ReactElement {
    const { id, label: storedLabel } = ext.parameters;
    const linkData = data.SACLinksDictionary?.[id];

    // Default values when link data is missing
    if (!linkData) {
        return (
            <LinkExtension
                href=""
                label=""
                shouldPreventRedirect={data.shouldPreventRedirect}
                contentLinkAnalytics={{ shouldSkip: true }}
            />
        );
    }

    const href = linkData.href || '';
    const label = storedLabel || linkData.label || '';

    let contentLinkAnalytics: ContentLinkAnalytics = { shouldSkip: true };

    // Only skip analytics on server-side (SSR), not if entryId is missing
    // linkContentAri and linkContentId are allowed to be undefined in the payload
    if (typeof window !== 'undefined') {
        try {
            // Increment position for each sac-link occurrence
            tracker.sacLinkPosition++;

            contentLinkAnalytics = {
                payload: {
                    linkType: 'text-link',
                    context: 'reference',
                    position: tracker.sacLinkPosition,
                    linkContentAri: getContentAri(linkData.entryId),
                    linkContentId: linkData.entryId,
                    linkContentResolvedUrl: href,
                    parentPageType: 'topic'
                },
                shouldSkip: false
            };
        } catch (error) {
            // Log error but don't break rendering
            // eslint-disable-next-line no-console
            console.warn('Failed to build analytics for sac-link', {
                id,
                error
            });
        }
    }

    return (
        <LinkExtension
            href={href}
            label={label}
            shouldPreventRedirect={data.shouldPreventRedirect}
            contentLinkAnalytics={contentLinkAnalytics}
        />
    );
}

/**
 * Handler: icon extension
 */
function handleIcon(ext: any, data: any): React.ReactElement {
    const { iconKey } = ext.parameters;
    return <IconExtension iconKey={iconKey} allIcons={data.allIcons} />;
}

/**
 * Handler: media extension
 */
function handleMedia(ext: any, data: any): React.ReactElement {
    const { mediaDictionary } = data;
    const mediaEntryId = get(ext.parameters, 'mediaEntryId');
    const imageData = mediaDictionary?.[mediaEntryId];

    const imgSrc = getLocalizedField(imageData, 'imageAsset.fields.file.url');
    const altTag = getLocalizedField(imageData, 'mediaEntry.fields.altTag');

    return <MediaExtension imgSrc={imgSrc} altTag={altTag} />;
}

/**
 * Handler: textFragment extension
 */
function handleTextFragment({
    ext,
    data,
    extensionsData,
    tracker
}: {
    ext: any;
    data: any;
    extensionsData: any;
    tracker: PositionTracker;
}): React.ReactElement {
    const { textFragmentDictionary } = data;
    const entryId = get(ext.parameters, 'textFragmentId');
    const textFragmentData = get(textFragmentDictionary, entryId);

    const adf = getLocalizedField(
        textFragmentData,
        'textFragmentEntry.fields.reusableText'
    );

    return (
        <TextFragmentExtension
            extensionHandlers={getExtensionHandlers(extensionsData, tracker)}
            adf={adf}
            isInline={ext.type === 'inlineExtension'}
        />
    );
}

/**
 * Extension handler router - delegates to specific handlers
 */
const getComAtlassianMesaHandler = (
    extensionsData: any,
    tracker: PositionTracker
) => {
    return (ext: any) => {
        const data = extensionsData[ext.extensionKey];

        // Route to appropriate handler based on extension type
        switch (ext.extensionKey) {
            case 'sac-link':
                return handleSacLink(ext, data, tracker);
            case 'icon':
                return handleIcon(ext, data);
            case 'media':
                return handleMedia(ext, data);
            case 'textFragment':
                return handleTextFragment({
                    ext,
                    data,
                    extensionsData,
                    tracker
                });
            default:
                return null;
        }
    };
};

export const getExtensionHandlers = (
    extensionsData: any,
    positionTracker?: PositionTracker
): RendererProps['extensionHandlers'] => {
    // Create tracker if not provided (top-level call)
    // Only initialize if on client-side
    const tracker = positionTracker || {
        sacLinkPosition: typeof window !== 'undefined' ? 0 : -1
    };

    return {
        'com.atlassian.mesa': getComAtlassianMesaHandler(
            extensionsData,
            tracker
        )
    };
};
